import type React from "react";

import { Link } from "react-router-dom";

import { cx, styled } from "simplydo/react/styled";
import { Label, SemanticCOLORS } from "simplydo/react/ui";

const WidgetContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;

  &.no-margin {
    padding: 0;
    margin-bottom: 0;
  }

  &:not(.no-margin) {
    padding: 0 0 20px;
    margin-bottom: 20px;
  }

  &:not(.no-border) {
    border-bottom: 1px solid #e3e4e5;
  }
`;

type RowComponentProps = React.PropsWithChildren<{
  $noMargin?: boolean;
  $noBorder?: boolean;
  style?: React.CSSProperties;
  className?: string;
}>;

const RowComponent = ({
  children,
  $noMargin,
  $noBorder,
  className,
  ...props
}: RowComponentProps) => (
  <WidgetContainer
    className={cx(
      $noMargin && "no-margin",
      $noBorder && "no-border",
      className,
    )}
    {...props}
  >
    {children}
  </WidgetContainer>
);

const Title = styled.h4`
  display: block;
  margin: 0;
  align-self: center;
  max-width: 300px;
  text-overflow: ellipsis;
  text-transform: capitalize;
  overflow: hidden;
  font-weight: 700;
  font-size: 1.07142857rem;
  color: black !important;

  &.is-linked {
    text-decoration-line: underline;
    text-decoration-style: dashed;
  }

  &.wraps {
    white-space: normal;
  }

  &:not(.wraps) {
    white-space: nowrap;
  }
`;

type RowTitleProps = {
  children: React.ReactNode;
  containerStyle?: React.CSSProperties;
  block?: boolean;
  linked?: boolean;
  wrapping?: boolean;
  to?: string;
  style?: React.CSSProperties;
};

const RowTitle = ({
  children,
  containerStyle,
  block,
  linked,
  wrapping,
  style,
  to,
}: RowTitleProps) => {
  const isLinked = linked || !!to;

  if (block) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          ...containerStyle,
        }}
      >
        <Title className={cx(wrapping && "wraps")} style={style}>
          {children}
        </Title>
      </div>
    );
  }
  return (
    <Title
      {...(isLinked ? { as: Link, to } : {})}
      className={cx(isLinked && "is-linked", wrapping && "wraps")}
      style={style}
    >
      {children}
    </Title>
  );
};

const TableCell = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 5px 10px 5px;
  width: 100%;
  border-radius: 10px;
  &:hover {
    background-color: #e3e4e5;
  }
`;

type RowTableCellProps = React.PropsWithChildren<{
  index?: number;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  style?: React.CSSProperties;
  as?: React.ElementType;
  to?: string;
  href?: string;
  target?: string;
  rel?: string;
  className?: string | null;
}>;

const RowTableCell = ({
  children,
  index: _index,
  className,
  ...props
}: RowTableCellProps) => (
  <TableCell {...props} className={className ?? undefined}>
    {children}
  </TableCell>
);

const HorizontalFlex = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  &.no-margin {
    margin-bottom: 0;
  }

  &:not(.no-margin) {
    margin-bottom: 10px;
  }
`;

const StatusArea = styled.div`
  color: #c0c0c0;
  display: flex;
  align-items: center;
  line-height: 13px;
  font-size: 14px;
  margin-top: 3px;
`;

const StyledLabel = styled(Label)`
  &&& {
    margin-right: 5px;
  }
`;

type StatusLabelProps = { color?: SemanticCOLORS; style?: React.CSSProperties };

const StatusLabel = ({ color, style, ...props }: StatusLabelProps) => (
  <StyledLabel
    {...props}
    style={{ ...style, marginRight: 5 }}
    circular
    color={color}
    empty
  />
);

const RowComponentFill = ({
  $noMargin,
  className,
  ...props
}: React.ComponentProps<typeof HorizontalFlex> & { $noMargin?: boolean }) => (
  <HorizontalFlex
    className={cx($noMargin && "no-margin", className)}
    {...props}
  />
);

RowComponent.Title = RowTitle;
RowComponent.Cell = RowTableCell;
RowComponent.Fill = RowComponentFill;
RowComponent.StatusArea = StatusArea;
RowComponent.StatusLabel = StatusLabel;

export default RowComponent;
